// ============================================
// Male Demo Data Source — ChatDataSource (mode: 'demo')
//
// Static implementation using real MF-typed data.
// Scenario: Male, 42, low T + fatigue + brain fog.
//
// Every value conforms to the same TypeScript
// interfaces as live Master Formulator output.
// ============================================

import type { BiomarkerResult } from '../../types';
import type {
  ChatDataSource,
  ChatMode,
  LabAnalystEvent,
} from '../../types/analystBar';
import type {
  CapturedSignal,
  CaptureCategory,
} from '../../types/fashionPlate';
import type {
  AnalystPhase2Output,
  ModifierSet,
  ProtocolItem,
} from '../../types/patientJourney';
import { geminiChat } from '../ai/providers/gemini';
import type { GeminiMessage } from '../ai/providers/gemini';
import { DEFAULT_BRAND_DISPLAY } from '../firestore/brandDisplayService';
import { mapBiomarkerToBarMarker, mapReportToPhase1 } from '../agents/labAnalystStream';

// ── Timing helpers ──

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// =============================================
// 1. MALE DEMO BIOMARKERS
// =============================================

export const MALE_DEMO_BIOMARKERS: BiomarkerResult[] = [
  // --- ATTENTION (low) ---
  {
    biomarker: 'Total Testosterone',
    value: 340,
    unit: 'ng/dL',
    standardRange: { low: 264, high: 916 },
    functionalRange: { low: 500, high: 900 },
    eliteAverage: 700,
    standardStatus: 'normal',
    hemologixStatus: 'low',
    category: 'hormonal',
    interpretation:
      'Total Testosterone at 340 ng/dL is within standard range (264-916) but critically below the functional optimal floor of 500. At 38% of optimal, this level is a primary driver of fatigue, brain fog, reduced motivation, and impaired recovery.',
  },
  {
    biomarker: 'Free Testosterone',
    value: 8.2,
    unit: 'pg/mL',
    standardRange: { low: 8.7, high: 25.1 },
    functionalRange: { low: 15, high: 25 },
    eliteAverage: 20,
    standardStatus: 'abnormal',
    hemologixStatus: 'low',
    category: 'hormonal',
    interpretation:
      'Free Testosterone at 8.2 pg/mL is below standard range and significantly below functional optimal (15-25). Combined with elevated SHBG, most of the already-low total testosterone is protein-bound and unavailable to tissues.',
  },
  {
    biomarker: 'Vitamin D (25-OH)',
    value: 24,
    unit: 'ng/mL',
    standardRange: { low: 30, high: 100 },
    functionalRange: { low: 50, high: 80 },
    eliteAverage: 62,
    standardStatus: 'abnormal',
    hemologixStatus: 'low',
    category: 'nutritional',
    interpretation:
      'Vitamin D at 24 ng/mL is below standard range and significantly below functional optimal (50-80). Vitamin D deficiency impairs testosterone synthesis, immune function, and mood regulation.',
  },

  // --- BORDERLINE ---
  {
    biomarker: 'SHBG',
    value: 52,
    unit: 'nmol/L',
    standardRange: { low: 10, high: 57 },
    functionalRange: { low: 20, high: 45 },
    eliteAverage: 35,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'hormonal',
    interpretation:
      'SHBG at 52 nmol/L is above the functional ceiling of 45. Elevated SHBG binds excess testosterone, reducing the bioavailable fraction and amplifying symptoms of low T even when total testosterone appears borderline.',
  },
  {
    biomarker: 'Cortisol (AM)',
    value: 22,
    unit: 'µg/dL',
    standardRange: { low: 6, high: 23 },
    functionalRange: { low: 10, high: 15 },
    eliteAverage: 12,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'hormonal',
    interpretation:
      'Morning cortisol at 22 µg/dL is within standard range but well above the functional ceiling of 15. Chronically elevated cortisol suppresses testosterone production and contributes to fatigue, poor sleep, and visceral fat accumulation.',
  },
  {
    biomarker: 'DHEA-S',
    value: 180,
    unit: 'µg/dL',
    standardRange: { low: 80, high: 560 },
    functionalRange: { low: 250, high: 400 },
    eliteAverage: 320,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'hormonal',
    interpretation:
      'DHEA-S at 180 µg/dL is below the functional floor of 250. As a precursor to testosterone and estrogen, low DHEA-S indicates adrenal fatigue compounding the hormonal picture.',
  },
  {
    biomarker: 'B12',
    value: 380,
    unit: 'pg/mL',
    standardRange: { low: 200, high: 900 },
    functionalRange: { low: 500, high: 800 },
    eliteAverage: 600,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'nutritional',
    interpretation:
      'B12 at 380 pg/mL is within standard range but below functional optimal of 500. Suboptimal B12 contributes to fatigue, brain fog, and impaired methylation — amplifying the effects of low testosterone.',
  },
  {
    biomarker: 'Magnesium (RBC)',
    value: 4.6,
    unit: 'mg/dL',
    standardRange: { low: 4.2, high: 6.8 },
    functionalRange: { low: 5.5, high: 6.5 },
    eliteAverage: 6.0,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'nutritional',
    interpretation:
      'RBC Magnesium at 4.6 mg/dL is below the functional floor of 5.5. Low magnesium impairs sleep quality, testosterone production, and is a required cofactor for Vitamin D metabolism.',
  },

  // --- OPTIMAL ---
  {
    biomarker: 'Fasting Glucose',
    value: 92,
    unit: 'mg/dL',
    standardRange: { low: 65, high: 99 },
    functionalRange: { low: 70, high: 85 },
    eliteAverage: 80,
    standardStatus: 'normal',
    hemologixStatus: 'optimal',
    category: 'metabolic',
    interpretation: 'Fasting glucose at 92 mg/dL is within standard range. No metabolic intervention needed.',
  },
  {
    biomarker: 'Hemoglobin A1c',
    value: 5.2,
    unit: '%',
    standardRange: { low: 4.0, high: 5.6 },
    functionalRange: { low: 4.5, high: 5.2 },
    eliteAverage: 5.0,
    standardStatus: 'normal',
    hemologixStatus: 'optimal',
    category: 'metabolic',
    interpretation: 'HbA1c at 5.2% is within both standard and functional ranges.',
  },
  {
    biomarker: 'hs-CRP',
    value: 0.6,
    unit: 'mg/L',
    standardRange: { low: 0, high: 3.0 },
    functionalRange: { low: 0, high: 0.5 },
    eliteAverage: 0.3,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'inflammatory',
    interpretation: 'hs-CRP at 0.6 mg/L is within standard range but slightly above the functional ceiling of 0.5. Mild systemic inflammation may be present.',
  },
  {
    biomarker: 'PSA',
    value: 0.8,
    unit: 'ng/mL',
    standardRange: { low: 0, high: 4.0 },
    functionalRange: { low: 0, high: 2.0 },
    eliteAverage: 0.7,
    standardStatus: 'normal',
    hemologixStatus: 'optimal',
    category: 'hormonal',
    interpretation: 'PSA at 0.8 ng/mL is well within range. No prostate concern — safe to proceed with hormonal support protocols.',
  },
];

// =============================================
// 2. MALE DEMO PROTOCOL (Gate 1 — Seed Supplements)
// =============================================

// Real prices from SEED_SUPPLEMENTS formulary:
// Tongkat Ali       → $32/mo (supp-tongkat-ali)
// Zinc + Copper     → $16/mo (supp-zinc-copper)
// Vitamin D3 + K2   → $22/mo (supp-vitamin-d3-k2)
// Ashwagandha KSM-66 → $28/mo (supp-ashwagandha-ksm66)
// Total: $98/mo → $3.27/day

const MALE_DEMO_PROTOCOL_ITEMS: ProtocolItem[] = [
  {
    productId: 'supp-tongkat-ali',
    productName: 'Tongkat Ali',
    dosage: '1 cap daily (AM with food)',
    timing: 'am_energy',
    reasoning:
      'Free Testosterone at 8.2 pg/mL and SHBG at 52 nmol/L indicate excessive testosterone binding. Tongkat ali reduces SHBG and increases free testosterone by 15-20%.',
    triggerBiomarker: 'Free Testosterone',
    tier: 1,
  },
  {
    productId: 'supp-zinc-copper',
    productName: 'Zinc + Copper',
    dosage: '1 cap daily (AM with food)',
    timing: 'am_energy',
    reasoning:
      'Total Testosterone at 340 ng/dL is below the functional floor of 500. Zinc is a required cofactor for testosterone synthesis and supports over 300 enzymatic reactions.',
    triggerBiomarker: 'Total Testosterone',
    tier: 1,
  },
  {
    productId: 'supp-vitamin-d3-k2',
    productName: 'Vitamin D3 + K2',
    dosage: '1 softgel daily (AM with fat-containing meal)',
    timing: 'am_energy',
    reasoning:
      'Vitamin D at 24 ng/mL is below standard range and significantly below functional optimal (50-80). Vitamin D deficiency directly impairs testosterone synthesis.',
    triggerBiomarker: 'Vitamin D (25-OH)',
    tier: 1,
  },
  {
    productId: 'supp-ashwagandha-ksm66',
    productName: 'Ashwagandha (KSM-66)',
    dosage: '1 cap daily (PM with dinner)',
    timing: 'pm_recovery',
    reasoning:
      'Morning cortisol at 22 µg/dL is well above functional optimal. KSM-66 reduces cortisol by 23-30% and supports testosterone levels, taken in the evening to enhance sleep quality.',
    triggerBiomarker: 'Cortisol (AM)',
    tier: 1,
  },
];

export const MALE_DEMO_PROTOCOL: AnalystPhase2Output = {
  protocol: MALE_DEMO_PROTOCOL_ITEMS,
  blockedProducts: [],
  warnings: [],
  modifiersApplied: {
    allergies: [],
    medications: [],
    conditions: [],
    lifestyle: [],
    budgetTier: 'all_tiers',
    intent: 'exploring',
    reproductiveStatus: 'not_applicable',
    priorSupplements: [],
    contraindications: [],
    goals: [],
  },
  gateLevel: 1,
};

// =============================================
// 3. DEMO LAB REPORT (for mapReportToPhase1)
// =============================================

const MALE_DEMO_LAB_REPORT = {
  id: 'demo-male-report-001',
  uploadedAt: new Date().toISOString(),
  source: 'pdf' as const,
  biomarkers: MALE_DEMO_BIOMARKERS,
};

// =============================================
// 4. SYMPTOM EXTRACTION (keyword-based)
// =============================================

interface SymptomPattern {
  regex: RegExp;
  term: string;
  category: CaptureCategory;
  linkedBiomarker?: string;
}

const MALE_SYMPTOM_PATTERNS: SymptomPattern[] = [
  {
    regex: /\b(wiped out|exhausted|tired|fatigue)\b/i,
    term: 'Fatigue',
    category: 'symptom',
    linkedBiomarker: 'Total Testosterone',
  },
  {
    regex: /\b(can'?t\s+focus|brain\s*fog|foggy)\b/i,
    term: 'Brain Fog',
    category: 'symptom',
    linkedBiomarker: 'Free Testosterone',
  },
  {
    regex: /\b(running on empty|no\s+energy|low\s+energy)\b/i,
    term: 'Low Energy',
    category: 'symptom',
    linkedBiomarker: 'Cortisol (AM)',
  },
  {
    regex: /\b(can'?t\s+sleep|sleep|insomnia)\b/i,
    term: 'Poor Sleep',
    category: 'symptom',
    linkedBiomarker: 'Cortisol (AM)',
  },
  {
    regex: /\b(libido|sex\s*drive|drive)\b/i,
    term: 'Low Libido',
    category: 'symptom',
    linkedBiomarker: 'Free Testosterone',
  },
];

function extractMaleSymptomsFromText(
  msg: string,
  turn: number,
  existing: CapturedSignal[],
): CapturedSignal[] {
  const existingTerms = new Set(existing.map((s) => s.term));
  const newSignals: CapturedSignal[] = [];

  for (const pattern of MALE_SYMPTOM_PATTERNS) {
    const match = msg.match(pattern.regex);
    if (!match) continue;

    const existingSignal = existing.find((s) => s.term === pattern.term);
    if (existingSignal) {
      if (!existingSignal.reinforcedAtTurns.includes(turn)) {
        existingSignal.reinforcedAtTurns.push(turn);
      }
      continue;
    }

    if (existingTerms.has(pattern.term)) continue;

    const signal: CapturedSignal = {
      id: `demo-male-${pattern.term.toLowerCase().replace(/\s+/g, '-')}-${turn}`,
      term: pattern.term,
      category: pattern.category,
      source: 'patient_mention',
      confidence: 0.95,
      detectedAtTurn: turn,
      reinforcedAtTurns: [],
      sourceText: match[1] || match[0],
      ...(pattern.linkedBiomarker
        ? { linkedBiomarker: pattern.linkedBiomarker }
        : {}),
    };

    newSignals.push(signal);
    existingTerms.add(pattern.term);
  }

  return [...existing, ...newSignals];
}

// =============================================
// 5. LAB ANALYSIS GENERATOR
// =============================================

async function* maleDemoAnalyzeLabs(
  _file: File,
): AsyncGenerator<LabAnalystEvent> {
  yield { type: 'status', message: 'Analyzing biomarkers against functional optimal ranges' };
  await delay(300);

  for (const bio of MALE_DEMO_BIOMARKERS) {
    const marker = mapBiomarkerToBarMarker(bio);
    yield { type: 'marker', marker };
    await delay(marker.flag === 'attention' ? 200 : 120);
  }

  yield { type: 'complete', report: mapReportToPhase1(MALE_DEMO_LAB_REPORT) };
}

// =============================================
// 6. EXPORTED ChatDataSource
// =============================================

export const maleDemoDataSource: ChatDataSource = {
  mode: 'demo' as ChatMode,

  brandDisplay: { ...DEFAULT_BRAND_DISPLAY },

  extractSymptoms: (
    msg: string,
    turn: number,
    existing: CapturedSignal[],
  ): Promise<CapturedSignal[]> => {
    return Promise.resolve(extractMaleSymptomsFromText(msg, turn, existing));
  },

  analyzeLabs: (file: File, _signals?: CapturedSignal[]): AsyncGenerator<LabAnalystEvent> => {
    return maleDemoAnalyzeLabs(file);
  },

  getProtocol: (
    _biomarkers: BiomarkerResult[],
    _modifiers: ModifierSet,
  ): AnalystPhase2Output => {
    return MALE_DEMO_PROTOCOL;
  },

  nurseRespond: (
    history: GeminiMessage[],
    systemPrompt: string,
    userMsg: string,
  ): Promise<string> => {
    return geminiChat('gemini-2.5-flash', systemPrompt, history, userMsg, 0.7, 512);
  },

  detectLateReveal: (
    _msg: string,
    _turn: number,
  ) => {
    return null;
  },

  getNurseReinforcement: () => {
    return '';
  },
};
